import React from 'react';

import { GameSelect } from '../../features/games/GameSelect';
import { GroupSelectModal } from '../../features/selection/GroupSelectModal';
import { MemberSelectModal } from '../../features/selection/MemberSelectModal';
import { useEventForm } from '../../hooks';
import { getResourceLabel, hasResource } from '../../resources/resources';
import { useAppSelector } from '../../store/hooks';
import type { RootState } from '../../store/store';
import { ConfirmSheet } from '../../ui/ConfirmSheet';

import {
  EventBasicInfoSection,
  EventTimeSection,
  EventSettingsSection,
  EventInviteSection,
} from './components';

import './EventFormPage.css';

export function EventFormPage() {
  const currentUserTelegramId = useAppSelector((s: RootState) => s.auth.user?.telegramId);
  const currentUser = useAppSelector((s: RootState) => s.auth.user);

  const form = useEventForm();

  const [memberModalOpen, setMemberModalOpen] = React.useState(false);
  const [groupModalOpen, setGroupModalOpen] = React.useState(false);
  const hiddenResourceOptionLabel = !hasResource(form.resourceId) ? getResourceLabel(form.resourceId) : null;

  return (
    <div className="ttbPage ttbEventFormPage">
      <ConfirmSheet
        open={form.cancelConfirmOpen}
        title="Отменить мероприятие?"
        message="Все участники будут уведомлены. Это действие нельзя отменить."
        confirmLabel={form.isSubmitting ? 'Отмена…' : 'Отменить'}
        destructive
        onCancel={() => {
          if (form.isSubmitting) return;
          form.setCancelConfirmOpen(false);
        }}
        onConfirm={() => {
          form.setCancelConfirmOpen(false);
          void form.onCancelEvent();
        }}
      />

      <div className="ttbFormContainer">
        <div className="ttbFormHeader">
          <button
            type="button"
            onClick={form.goBack}
            disabled={form.isSubmitting}
            className="ttbButton ttbButtonOutlineHeader"
          >
            ← Назад
          </button>
          <div className="ttbFormHeaderTitle">
            {form.isEdit ? 'Редактировать' : 'Создать приключение'}
          </div>
          <button
            type="button"
            className="ttbButton ttbButtonOutlineHeader ttbFormHeaderSpacer"
            disabled
            aria-hidden="true"
            tabIndex={-1}
          >
            ← Назад
          </button>
        </div>

        <EventBasicInfoSection
          title={form.title}
          description={form.description}
          resourceId={form.resourceId}
          type={form.type}
          isSubmitting={form.isSubmitting}
          titleError={form.titleError}
          hiddenResourceOptionLabel={hiddenResourceOptionLabel}
          unavailableVipResourceIds={form.unavailableVipResourceIds}
          allowedResourceIds={form.guestOpenResourceIds}
          onTitleChange={form.setTitle}
          onTitleBlur={() => form.setTouched((t) => ({ ...t, title: true }))}
          onDescriptionChange={form.setDescription}
          onResourceChange={form.setResourceId}
        />

        {form.guestAccessHint ? <div className="ttbFieldHint ttbEventFormNotice">{form.guestAccessHint}</div> : null}

        {form.resourceNotice ? (
          <div className="ttbFieldHint ttbEventFormNotice">{form.resourceNotice}</div>
        ) : null}

        {form.type === 'boardgames' && (
          <div className="ttbFormSection">
            <div className="ttbFormSectionHeader">
              <span className="ttbFormSectionIcon">🎲</span>
              <span className="ttbFormSectionTitle">Игра</span>
            </div>
            <GameSelect selectedGameId={form.gameId} onGameChange={form.setGameId} />
          </div>
        )}

        <EventTimeSection
          dateValue={form.dateValue}
          startTime={form.startTime}
          endTime={form.endTime}
          today={form.today}
          isSubmitting={form.isSubmitting}
          dateError={form.dateError}
          timeError={form.timeError}
          onDateChange={form.setSelectedDate}
          onDateBlur={() => form.setTouched((t) => ({ ...t, date: true }))}
          onStartTimeChange={(value) => {
            form.setTouched((t) => ({ ...t, startTime: true }));
            form.updateStartTime(value);
          }}
          onStartTimeBlur={() => form.setTouched((t) => ({ ...t, startTime: true }))}
          onEndTimeChange={(value) => {
            form.setTouched((t) => ({ ...t, endTime: true }));
            form.setEndTime(value);
          }}
          onEndTimeBlur={() => form.setTouched((t) => ({ ...t, endTime: true }))}
        />

        <EventSettingsSection
          maxParticipants={form.maxParticipants}
          minMaxParticipants={form.minMaxParticipants}
          maxParticipantsLimit={form.maxParticipantsLimit}
          visibility={form.visibility}
          isSubmitting={form.isSubmitting}
          maxParticipantsError={form.maxParticipantsError}
          onMaxParticipantsChange={(value) => form.setMaxParticipants(value)}
          onMaxParticipantsBlur={() => form.setTouched((t) => ({ ...t, maxParticipants: true }))}
          onVisibilityChange={form.setVisibility}
        />

        <EventInviteSection
          invitedUsers={form.invitedUsers}
          isSubmitting={form.isSubmitting}
          onOpenMemberModal={() => setMemberModalOpen(true)}
          onOpenGroupModal={() => setGroupModalOpen(true)}
          onRemoveUser={form.handleRemoveUser}
          reservedSlotsCount={form.reservedSlotsCount}
          currentUser={currentUser}
          onAddReservedSlot={form.handleAddReservedSlot}
          onRemoveReservedSlot={form.handleRemoveReservedSlot}
        />

        <div className="ttbFormActions">
          <button
            type="button"
            onClick={form.goBack}
            disabled={form.isSubmitting}
            className="ttbButton ttbButtonOutlineHeader"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={form.onSubmit}
            disabled={!form.isValid || form.isSubmitting}
            className="ttbButton ttbButtonSubmit"
          >
            {form.isSubmitting
              ? form.isEdit
                ? 'Сохранение…'
                : 'Создание…'
              : form.isEdit
                ? '✨ Сохранить'
                : '✨ Создать приключение'}
          </button>
        </div>

        {form.isEdit ? (
          <button
            type="button"
            onClick={() => form.setCancelConfirmOpen(true)}
            disabled={form.isSubmitting}
            className="ttbButton ttbDangerAction ttbCancelEventButton"
          >
            Отменить мероприятие
          </button>
        ) : null}
      </div>

      {/* Modals */}
      <MemberSelectModal
        isOpen={memberModalOpen}
        onClose={() => setMemberModalOpen(false)}
        onSelect={form.handleAddMember}
        excludeIds={[...form.invitedUserIds, ...(currentUserTelegramId ? [currentUserTelegramId] : [])]}
      />
      <GroupSelectModal
        isOpen={groupModalOpen}
        onClose={() => setGroupModalOpen(false)}
        onSelect={form.handleAddGroup}
      />
    </div>
  );
}
